import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  GroupWithProgress,
  StudentWithProgress,
  getUserProgressByChapterMapper,
  getGroupProgressByChapterMapper,
} from 'src/config/api/models/Progress';
import apiRoutes, { baseURL } from 'src/config/api/routes';
import { RootState } from 'src/modules/shared/store';

type getGroupProgressByChapterParams = {
  bookId: string;
  chapterId: string;
  groupId: string;
};

type getStudentProgressByChapterParams = {
  bookId: string;
  chapterId: string;
  groupId: string;
  studentId: string;
};

type patchPageProgressParams = {
  trajectoryId: string;
  pageId: string;
};

type getUserProgressByChapterParams = {
  bookId: string;
  chapterId: string;
};

type setTrajectoryProgressParams = {
  trajectoryId: string;
  type: 'finished' | 'unfinished';
};

export const progressApi = createApi({
  reducerPath: 'progressApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.jwt;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getGroupProgressByChapter: builder.mutation<GroupWithProgress, getGroupProgressByChapterParams>({
      query: ({ bookId, chapterId, groupId }) => ({
        url: apiRoutes.groupProgressByChapter(groupId, bookId, chapterId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getGroupProgressByChapterMapper(response)
    }),
    getStudentProgressByChapter: builder.mutation<
      StudentWithProgress,
      getStudentProgressByChapterParams
    >({
      query: ({ bookId, chapterId, groupId, studentId }) => ({
        url: apiRoutes.studentProgressByChapter(groupId, bookId, chapterId, studentId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getUserProgressByChapterMapper(response),
    }),
    patchPageProgress: builder.mutation<void, patchPageProgressParams>({
      query: ({ trajectoryId, pageId }) => ({
        url: apiRoutes.pageProgressByTrajectory(trajectoryId, pageId),
        method: 'PATCH',
      }),
    }),
    getUserProgressByChapter: builder.query<StudentWithProgress, getUserProgressByChapterParams>({
      query: ({ bookId, chapterId }) => ({
        url: apiRoutes.userProgressByChapter(bookId, chapterId),
        method: 'GET',
      }),
      transformResponse: (response: any) => getUserProgressByChapterMapper(response),
    }),
    setTrajectoryProgress: builder.mutation<void, setTrajectoryProgressParams>({
      query: ({ trajectoryId, type }) => ({
        url: apiRoutes.trajectoryProgressByTrajectory(trajectoryId),
        method: 'POST',
        body: {
          type,
        },
      }),
    }),
  }),
});

export const {
  useGetGroupProgressByChapterMutation,
  useGetStudentProgressByChapterMutation,
  usePatchPageProgressMutation,
  useGetUserProgressByChapterQuery,
  useSetTrajectoryProgressMutation,
} = progressApi;
